import { resolveConfig, type AppConfig } from "@studystraight/shared-config";
import type { CameraPlacement, VisionResult } from "@studystraight/shared-types";
import {
  assessDetectionQuality,
  type DetectionQuality,
  type VisionProviderCapabilities,
} from "@studystraight/vision-core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  createInferenceLoop,
  type PerfSnapshot,
} from "../platform/vision/InferenceLoop.js";
import {
  createMediaPipeWebVisionProvider,
  VisionGlUnavailableError,
} from "../platform/vision/MediaPipeWebVisionProvider.js";

/**
 * Vision hỏng theo cách mà UI **phải** nói ra.
 *
 * `stage` để phân biệt ba kiểu hỏng khác nhau về nguyên nhân (và về cách người dùng xử lý), còn
 * `detail` là thông tin kỹ thuật — chỉ hiện khi bật Debug Mode, không đưa vào câu nói với trẻ (§20).
 */
export interface VisionFailure {
  /**
   * `webgl` tách riêng khỏi `init` vì đây là kiểu hỏng duy nhất **không** phải lỗi của app và
   * người dùng làm được gì đó với nó (tắt Chế độ khoá, đóng tab khác, mở lại Safari) — xem
   * `visionMessages.webglBlocked`.
   */
  stage: "init" | "webgl" | "timeout" | "stalled" | "runtime";
  detail: string;
}

export interface VisionTrackingState {
  ready: boolean;
  /** Frame mới nhất — đã throttle theo `uiUpdateFps`, KHÔNG phải mọi frame inference. */
  result: VisionResult | null;
  quality: DetectionQuality | null;
  perf: PerfSnapshot | null;
  /**
   * `null` khi vision còn khoẻ.
   *
   * Trước đây field này tên `initError` và **không màn hình nào đọc nó**: MediaPipe chết là app
   * đứng ở "Đang tải bộ nhận diện tư thế…" mãi mãi, nút hiệu chỉnh không bao giờ bật, và không có
   * cách nào biết vì sao. Đổi tên + có consumer là một phần của bản sửa, không phải cosmetic.
   */
  error: VisionFailure | null;
  /** Dựng lại provider từ đầu. Rẻ hơn reload trang: camera đang chạy không bị đóng lại. */
  retry: () => void;
  config: AppConfig;
  /** Engine/delegate thực tế sau fallback — Debug Mode (§25) cần để giải thích máy chậm. */
  capabilities: VisionProviderCapabilities | null;
}

/** Số lần `detect()` lỗi liên tiếp mới coi là hỏng thật (một lỗi lẻ có thể do frame xấu). */
const RUNTIME_ERROR_TOLERANCE = 3;

function describeError(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === "string") return err;
  return String(err);
}

/**
 * Emscripten đã `abort()` bên trong wasm — ví dụ `Aborted(). Build with -sASSERTIONS for more info.`
 *
 * Khác mọi lỗi runtime khác ở một điểm quyết định: module **chết hẳn**, không hồi phục. Đợi đủ
 * `RUNTIME_ERROR_TOLERANCE` lần rồi mới báo là đợi vô nghĩa, và mỗi lần gọi thêm chỉ abort lại.
 */
function isWasmAbort(err: unknown): boolean {
  return /\bAborted\(/.test(describeError(err));
}

export interface UseVisionTrackingOptions {
  placement: CameraPlacement;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  /** Bật/tắt vòng lặp (ví dụ: tắt khi camera lỗi hoặc tab ẩn). */
  enabled: boolean;
  /** Nhận **mọi** frame, không throttle — dùng cho engine/ring buffer ở các bước sau. */
  onFrame?: (result: VisionResult, inferenceMs: number) => void;
}

/**
 * Ghép provider + inference loop + đánh giá chất lượng khung hình.
 *
 * §31: vision chạy 8–15 FPS nhưng UI chỉ update 2–5 FPS. Throttle nằm ở đây (tầng adapter),
 * không ở trong React: nếu setState mỗi frame inference, React re-render 12 lần/giây và cả
 * app nóng máy vì render chứ không vì inference.
 */
export function useVisionTracking({
  placement,
  videoRef,
  enabled,
  onFrame,
}: UseVisionTrackingOptions): VisionTrackingState {
  const config = useMemo(() => resolveConfig(placement), [placement]);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<VisionFailure | null>(null);
  const [result, setResult] = useState<VisionResult | null>(null);
  const [quality, setQuality] = useState<DetectionQuality | null>(null);
  const [perf, setPerf] = useState<PerfSnapshot | null>(null);
  const [capabilities, setCapabilities] = useState<VisionProviderCapabilities | null>(null);
  const [attempt, setAttempt] = useState(0);
  /**
   * Bản GPU đã abort giữa inference → dựng lại ở CPU. Một chiều, không bao giờ quay lại GPU trong
   * cùng một lần mở app: GPU đã abort một lần thì lần sau cũng vậy, và mỗi lần thử lại là ~2 giây
   * camera đứng hình cộng một wasm module 11.7 MB nữa trên máy vốn đã yếu.
   */
  const [forceCpu, setForceCpu] = useState(false);

  const retry = useCallback(() => {
    setError(null);
    setAttempt((n) => n + 1);
  }, []);

  // Giữ callback/config trong ref để không phải dựng lại provider mỗi lần parent re-render:
  // khởi tạo lại PoseLandmarker mất ~1–2 giây và làm camera đứng hình.
  const onFrameRef = useRef(onFrame);
  onFrameRef.current = onFrame;
  const configRef = useRef(config);
  configRef.current = config;

  useEffect(() => {
    if (!enabled) return;

    let disposed = false;
    const provider = createMediaPipeWebVisionProvider({
      placement,
      gpuInitTimeoutMs: config.vision.gpuInitTimeoutMs,
      forceCpu,
    });
    const uiIntervalMs = 1000 / config.vision.uiUpdateFps;
    let lastUiAt = 0;
    let initialized = false;
    let consecutiveErrors = 0;
    /** Lần cuối `detect()` trả về (kể cả trả null) — mốc để phát hiện vòng lặp đứng. */
    let lastActivityAt = performance.now();

    /** Mirror của state để chỉ gọi setState khi thực sự đổi (onPerf chạy 12 lần/giây). */
    let failure: VisionFailure | null = null;
    const fail = (next: VisionFailure | null): void => {
      if (disposed || failure?.stage === next?.stage) return;
      failure = next;
      setError(next);
    };

    const loop = createInferenceLoop({
      provider,
      config: config.vision,
      getVideo: () => videoRef.current,
      onResult: (visionResult, inferenceMs) => {
        onFrameRef.current?.(visionResult, inferenceMs);
        if (visionResult.timestamp - lastUiAt < uiIntervalMs) return;
        lastUiAt = visionResult.timestamp;
        setResult(visionResult);
        setQuality(assessDetectionQuality(visionResult, configRef.current));
      },
      onPerf: (snapshot) => {
        lastActivityAt = performance.now();
        consecutiveErrors = 0;
        // Frame lại về được thì xoá cảnh báo "đứng"/"lỗi": vision tự hồi phục là chuyện bình
        // thường (tab quay lại foreground, camera bị chiếm rồi nhả).
        fail(null);
        setPerf(snapshot);
      },
      onError: (err) => {
        lastActivityAt = performance.now();
        consecutiveErrors += 1;

        if (isWasmAbort(err)) {
          // Dừng vòng lặp: module đã chết, gọi tiếp chỉ sinh ra hàng nghìn lần abort giống nhau.
          loop.stop();
          const wasGpu = provider.capabilities.delegate === "GPU";
          fail({
            stage: "runtime",
            detail:
              `${describeError(err)} — wasm abort ở delegate ${provider.capabilities.delegate}` +
              ` (${provider.delegateReason})` +
              (provider.glContextLost ? ", GL context đã mất giữa phiên" : ", GL context còn sống") +
              (wasGpu && !forceCpu ? ", đang dựng lại bằng CPU" : ""),
          });
          // GPU abort là ca đã gặp trên iPhone thật: hạ xuống CPU một lần, tự động, không bắt
          // người dùng bấm "Thử lại" cho một thứ mà bấm lại cũng ra đúng kết quả đó.
          if (wasGpu && !forceCpu) {
            setForceCpu(true);
            setAttempt((n) => n + 1);
          }
          return;
        }

        if (consecutiveErrors >= RUNTIME_ERROR_TOLERANCE) {
          fail({ stage: "runtime", detail: describeError(err) });
        }
      },
    });

    provider
      .initialize()
      .then(() => {
        if (disposed) return;
        initialized = true;
        lastActivityAt = performance.now();
        setCapabilities(provider.capabilities);
        setReady(true);
        loop.start();
      })
      .catch((err: unknown) => {
        // Lỗi GL đã tự nói rõ nó xảy ra ở đâu; dán thêm `initStage` chỉ lặp lại đúng câu đó lần thứ
        // hai trong cùng một dòng — mà đây lại là dòng duy nhất người kiểm đọc được trên điện thoại.
        if (err instanceof VisionGlUnavailableError) {
          fail({ stage: "webgl", detail: describeError(err) });
          return;
        }
        fail({ stage: "init", detail: `${describeError(err)} (ở bước: ${provider.initStage})` });
      });

    // Watchdog: hai kiểu "im lặng" mà promise ở trên không bắt được — initialize() treo không
    // reject, và vòng lặp chạy nhưng không frame nào tới đích. Cả hai đều biểu hiện y như nhau
    // trên máy người dùng ("camera lên, không nhận tư thế") nên phải nói ra được là kiểu nào.
    const startedAt = performance.now();
    const watchdog = setInterval(() => {
      if (disposed) return;
      const idleMs = performance.now() - lastActivityAt;
      if (!initialized) {
        if (performance.now() - startedAt > config.vision.initTimeoutMs) {
          fail({
            stage: "timeout",
            detail:
              `initialize() chưa xong sau ${String(Math.round(config.vision.initTimeoutMs / 1000))}s` +
              ` — đang ở bước: ${provider.initStage}`,
          });
        }
        return;
      }
      if (idleMs > config.vision.stallTimeoutMs) {
        fail({
          stage: "stalled",
          detail: `không có frame nào trong ${String(Math.round(idleMs / 1000))}s`,
        });
      }
    }, 1000);

    return () => {
      disposed = true;
      clearInterval(watchdog);
      loop.stop();
      void provider.dispose();
      setReady(false);
    };
    // `attempt` trong deps: đổi nó là cách duy nhất để dựng lại provider sau khi retry.
  }, [attempt, config, enabled, forceCpu, placement, videoRef]);

  return { ready, result, quality, perf, error, retry, config, capabilities };
}
